/**
 * TranslationMemoryPanel.tsx — PLAT-10850 (epic PLAT-10825, E13)
 *
 * The Translation Review workspace's "TM" tab body. Lists every ACTIVE fragment
 * the backend marked as translation-memory-backed (`phrase.tmEntryIds` non-empty
 * — honored-only, PLAT-10843), showing for each the original (source) text, the
 * translated text, and — best-effort — the applied glossary term(s) resolved
 * against the workspace's `translation_memory_entry` records (`entriesById`,
 * loaded once by the workspace and shared with the aligned view's term
 * underline). Clicking a card seeks the playhead to that fragment (moving both
 * aligned panes' cursors), the same cross-link the Changes tab uses.
 *
 * Mirrors `TranslationChangesPanel` (entry-build loop → cards → seek), but
 * display-only: TM membership is machine provenance, not something edited here.
 * Term-label resolution is pure enrichment — if `entriesById` is empty or an id
 * no longer resolves (entry later deleted), the card still lists the fragment
 * and the highlight still renders; the tab never hard-depends on the Settings
 * stack (E13 / DS5).
 */

import {
  useTranscription,
  useTranslationStore,
} from '@src/stores-v2/StoreContext';
import { convertTimeToMMSS } from '@src/utility/timeFormat';
import { seekToInitialIndex } from '@src/utility/transcriptEvents';
import { observer } from 'mobx-react-lite';
import React, { useState } from 'react';
import styled from 'styled-components';

import { fragmentKeyOf, resolveFragmentTmEntry } from './changedPhraseKit';
import {
  Card,
  Empty,
  Field,
  FieldLabel,
  List,
  Meta,
  NowValue,
  OriginalValue,
} from './reviewCardPrimitives';
import { buildTmCards } from './translationMemoryCards';

import type { TmPromoteSeed } from './changedPhraseKit';
import type { TranslationMemoryEntry } from '@src/repositories/TranslationMemoryEntryRepository';
import type { ITranscriptVideoCreator } from '@src/stores/ITranscriptVideoCreator';

interface TranslationMemoryPanelProps {
  language: string;
  /**
   * The scoped video creator for this review surface. Clicking a card seeks its
   * playhead to the source word, which moves the cursor in both aligned panes.
   */
  videoCreator: ITranscriptVideoCreator;
  /**
   * The workspace's glossary entries keyed by id (loaded once, shared with the
   * aligned view's term underline). Best-effort: an empty map (not loaded / no
   * showcase) degrades cards to the generic "Translation memory" label — the
   * list still renders. (DS5)
   */
  entriesById: ReadonlyMap<string, TranslationMemoryEntry>;
  /**
   * Collection id → name, for the per-entry "Details" disclosure (PLAT-10878).
   * Best-effort like `entriesById`: a missing id renders as the "Default" bucket.
   */
  collectionNameById?: ReadonlyMap<string, string>;
  /**
   * Open the entry editor for a resolved TM entry (PLAT-10878). The host owns the
   * modal + save + reload; the panel just requests the edit. Absent ⇒ no Edit
   * affordance (the Details view is then read-only).
   */
  onEditEntry?: (entry: TranslationMemoryEntry) => void;
  /**
   * Save this fragment's translation as a NEW translation-memory entry
   * (PLAT-10852) — the same promote flow the edit overlay offers, so a reviewer
   * working from this list doesn't have to go find the fragment in the
   * transcript first. The host opens the seeded entry editor. Absent (flag off /
   * no workspace) ⇒ no affordance.
   */
  onPromoteToTm?: (seed: TmPromoteSeed) => void;
  /**
   * Entry ids promoted in this session, keyed by `fragmentKeyOf` (PLAT-10852).
   * With the card's own backend `tmEntryIds`, this decides whether the card
   * shows the "Translation Memory Entry" link or the promote action.
   */
  promotedTmEntryIds?: ReadonlyMap<string, string>;
  /** Open the entry behind a card (PLAT-10852). The host owns the modal. */
  onViewTmEntry?: (entry: TranslationMemoryEntry) => void;
  /**
   * Hovering a card reports the fragment's source indices (null on leave) so the
   * host can paint the correspondence highlight in both aligned columns — the
   * same channel the Changes tab uses. Optional / opt-in.
   */
  onEntryHover?: (originalIndices: readonly number[] | null) => void;
  /**
   * Clicking a card also asks the host to scroll both aligned columns to the
   * fragment (the playback autoscroll is playing-only, so a paused seek alone
   * leaves it offscreen). Optional / opt-in.
   */
  onEntryReveal?: (initialIndex: number) => void;
}

export const TranslationMemoryPanel: React.FC<TranslationMemoryPanelProps> =
  observer(function TranslationMemoryPanel({
    language,
    videoCreator,
    entriesById,
    collectionNameById,
    onEditEntry,
    onPromoteToTm,
    promotedTmEntryIds,
    onViewTmEntry,
    onEntryHover,
    onEntryReveal,
  }) {
    const translationStore = useTranslationStore();
    const transcriptionStore = useTranscription();
    // Which cards have their provenance "Details" expanded (PLAT-10878). Keyed by
    // the card's stable source-index key so several can be open at once.
    const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set());
    const toggleExpanded = (key: string) =>
      setExpanded(prev => {
        const next = new Set(prev);
        next.has(key) ? next.delete(key) : next.add(key);
        return next;
      });

    const baseElements = transcriptionStore.getFinalTranscriptionElements();
    const cards = buildTmCards(
      translationStore.getSegments(language),
      entriesById,
      baseElements,
      collectionNameById,
      // A term promoted this session has no backend `tmEntryIds` yet, so without
      // this it wouldn't appear here until a re-translate (PLAT-10852).
      promotedTmEntryIds,
    );

    if (cards.length === 0) {
      return <Empty>No translation-memory terms in this translation.</Empty>;
    }

    return (
      <List>
        {cards.map(card => {
          const seekable = card.seekIndex !== undefined;
          const seek = () => {
            if (card.seekIndex === undefined) return;
            seekToInitialIndex(videoCreator, baseElements, card.seekIndex);
            onEntryReveal?.(card.seekIndex);
          };
          const isOpen = expanded.has(card.key);
          // The entry behind this card — its first resolvable backend id, or a
          // promotion made this session (PLAT-10852).
          const cardEntry = resolveFragmentTmEntry(
            fragmentKeyOf(card.originalIndices),
            card.terms.map(t => t.id),
            entriesById,
            promotedTmEntryIds,
          );
          return (
            <Card
              key={card.key}
              data-entry-key={card.key}
              $clickable={seekable}
              role={seekable ? 'button' : undefined}
              tabIndex={seekable ? 0 : undefined}
              title={
                seekable ? 'Jump to this spot in the transcript' : undefined
              }
              onClick={seekable ? seek : undefined}
              onMouseEnter={
                onEntryHover
                  ? () => onEntryHover(card.originalIndices)
                  : undefined
              }
              onMouseLeave={onEntryHover ? () => onEntryHover(null) : undefined}
              onKeyDown={
                seekable
                  ? e => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        seek();
                      }
                    }
                  : undefined
              }
            >
              <Field>
                <FieldLabel>Original Text</FieldLabel>
                <OriginalValue>{card.source}</OriginalValue>
              </Field>

              <Field>
                <FieldLabel>Translation</FieldLabel>
                <NowValue>{card.translated}</NowValue>
              </Field>

              <Field>
                <FieldLabel>
                  {card.tmEntryIdCount > 1 ? 'Glossary terms' : 'Glossary term'}
                </FieldLabel>
                {card.terms.length > 0 ? (
                  <TermList>
                    {card.terms.map(term => (
                      <TermChip key={term.id}>
                        {term.isDnt ? (
                          <>
                            {term.sourceTerm}
                            <TermNote> · do not translate</TermNote>
                          </>
                        ) : (
                          <>
                            {term.sourceTerm}
                            <TermArrow> → </TermArrow>
                            {term.translation}
                          </>
                        )}
                      </TermChip>
                    ))}
                  </TermList>
                ) : (
                  // Enrichment unavailable (entries not loaded / id unresolved):
                  // still show the fragment is TM-backed (DS5).
                  <TermFallback>
                    {card.tmEntryIdCount > 1
                      ? `${card.tmEntryIdCount} translation-memory terms`
                      : 'Translation memory'}
                  </TermFallback>
                )}
              </Field>

              {/* PLAT-10878: collapsed provenance drill-in. Only when entries
                  resolved (else the fallback label above stands, nothing to open).
                  stopPropagation so the toggle/Edit never trigger the card seek. */}
              {card.terms.length > 0 && (
                <Details onClick={e => e.stopPropagation()}>
                  <DetailsToggle
                    type="button"
                    aria-expanded={isOpen}
                    onClick={e => {
                      e.stopPropagation();
                      toggleExpanded(card.key);
                    }}
                  >
                    <Chevron $open={isOpen} aria-hidden="true">
                      ▸
                    </Chevron>
                    {isOpen ? 'Hide details' : 'Details'}
                  </DetailsToggle>
                  {isOpen && (
                    <DetailsBody>
                      {card.terms.map(term => (
                        <EntryDetail key={term.id}>
                          <DetailRow>
                            <DetailLabel>From Text</DetailLabel>
                            <DetailValue>{term.sourceTerm}</DetailValue>
                          </DetailRow>
                          <DetailRow>
                            <DetailLabel>To Text</DetailLabel>
                            <DetailValue>{term.translation}</DetailValue>
                          </DetailRow>
                          <DetailRow>
                            <DetailLabel>Collection</DetailLabel>
                            <DetailValue>
                              {term.collectionName ?? 'Default'}
                            </DetailValue>
                          </DetailRow>
                          {onEditEntry && (
                            <CardActionButton
                              type="button"
                              onClick={e => {
                                e.stopPropagation();
                                const entry = entriesById.get(term.id);
                                if (entry) onEditEntry(entry);
                              }}
                            >
                              Edit
                            </CardActionButton>
                          )}
                        </EntryDetail>
                      ))}
                    </DetailsBody>
                  )}
                </Details>
              )}

              {/* PLAT-10852 — the same affordance the edit overlay carries: a
                  link to the entry when the fragment IS one, otherwise the
                  promote action seeded with the card's own texts.
                  stopPropagation so neither triggers the card seek. */}
              {cardEntry && onViewTmEntry ? (
                <CardActionButton
                  type="button"
                  onClick={e => {
                    e.stopPropagation();
                    onViewTmEntry(cardEntry);
                  }}
                >
                  Translation Memory Entry
                </CardActionButton>
              ) : (
                onPromoteToTm && (
                  <CardActionButton
                    type="button"
                    onClick={e => {
                      e.stopPropagation();
                      onPromoteToTm({
                        fragmentKey: fragmentKeyOf(card.originalIndices),
                        sourceTerm: card.source,
                        translation: card.translated,
                      });
                    }}
                  >
                    Save to Translation Memory
                  </CardActionButton>
                )
              )}

              {card.seekTs !== undefined && (
                <Meta>{convertTimeToMMSS(card.seekTs)}</Meta>
              )}
            </Card>
          );
        })}
      </List>
    );
  });

const TermList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
`;

// A glossary-term chip. Gold accent to match the term underline on the
// transcript word (see AlignedBlocksView `TmTermMark`).
const TermChip = styled.span`
  font-size: var(--text-xs);
  line-height: 1.4;
  color: var(--foreground);
  background: color-mix(in srgb, var(--accent) 14%, transparent);
  border: 1px solid color-mix(in srgb, var(--accent) 40%, transparent);
  border-radius: 4px;
  padding: 1px 6px;
`;

const TermArrow = styled.span`
  color: var(--text-muted);
`;

const TermNote = styled.span`
  color: var(--text-muted);
`;

const TermFallback = styled.div`
  font-size: var(--text-xs);
  line-height: 1.4;
  color: var(--text-muted);
`;

// PLAT-10878 — provenance drill-in. Collapsed by default; the reviewer opens it
// only when reconciling which entry drove the translation.
const Details = styled.div`
  margin-top: 4px;
`;

const DetailsToggle = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: none;
  border: none;
  padding: 2px 0;
  cursor: pointer;
  font-size: var(--text-xs);
  color: var(--text-muted);
  &:hover {
    color: var(--foreground);
  }
`;

const Chevron = styled.span<{ $open: boolean }>`
  display: inline-block;
  transition: transform 0.12s ease;
  transform: rotate(${p => (p.$open ? 90 : 0)}deg);
`;

const DetailsBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 6px;
`;

const EntryDetail = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
  padding: 8px;
  border: 1px solid
    var(--border, color-mix(in srgb, var(--foreground) 12%, transparent));
  border-radius: 6px;
  background: color-mix(in srgb, var(--foreground) 4%, transparent);
`;

const DetailRow = styled.div`
  display: flex;
  gap: 8px;
  font-size: var(--text-xs);
  line-height: 1.4;
`;

const DetailLabel = styled.span`
  flex: 0 0 84px;
  color: var(--text-muted);
`;

const DetailValue = styled.span`
  color: var(--foreground);
  word-break: break-word;
`;

// Shared ghost action for the card: the per-entry "Edit" inside Details
// (PLAT-10878) and the card-level "Save to Translation Memory" (PLAT-10852).
// Green `--primary` per the design system's action color.
const CardActionButton = styled.button`
  align-self: flex-start;
  margin-top: 4px;
  background: none;
  border: 1px solid color-mix(in srgb, var(--primary) 45%, transparent);
  color: var(--primary);
  border-radius: 4px;
  padding: 2px 10px;
  font-size: var(--text-xs);
  cursor: pointer;
  &:hover {
    background: color-mix(in srgb, var(--primary) 12%, transparent);
  }
`;
